import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { waybillAPI } from '../../api/waybillAPI';

const fmtCurrency = (n) =>
  typeof n === 'number'
    ? n.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-PH', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—';

const fmtLong = (dateStr) =>
  new Date(dateStr + 'T00:00:00').toLocaleDateString('en-PH', { month: 'long', day: 'numeric', year: 'numeric' });

export default function DailyWaybillPrintPage() {
  const [searchParams] = useSearchParams();
  const dateFrom = searchParams.get('dateFrom') || new Date().toISOString().split('T')[0];
  const dateTo = searchParams.get('dateTo') || dateFrom;
  const shipmentType = searchParams.get('shipmentType') || '';

  const [waybills, setWaybills] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    waybillAPI.getDailyWaybill({ dateFrom, dateTo, shipmentType })
      .then((res) => {
        setWaybills(Array.isArray(res.data?.data) ? res.data.data : []);
        setLoading(false);
        setTimeout(() => window.print(), 400);
      })
      .catch(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    const label = dateFrom === dateTo ? fmtLong(dateFrom) : `${dateFrom} to ${dateTo}`;
    document.title = `Daily Waybill Report — ${label}`;
  }, [dateFrom, dateTo]);

  const dateLabel = dateFrom === dateTo
    ? fmtLong(dateFrom)
    : `${fmtLong(dateFrom)} – ${fmtLong(dateTo)}`;

  const delivered = waybills.filter((w) => w.IsDelivered).length;
  const pending = waybills.length - delivered;
  const periodTotal = waybills.reduce((s, w) => s + (typeof w.amountDue === 'number' ? w.amountDue : 0), 0);

  if (loading) return <p style={{ padding: 40 }}>Loading report…</p>;

  return (
    <>
      <style>{`
        body { font-family: Arial, sans-serif; font-size: 10pt; margin: 0; }
        @page { size: A4 landscape; margin: 12mm; }
        @media print { .no-print { display: none !important; } }
        .header { text-align: center; margin-bottom: 8px; }
        h1 { font-size: 14pt; margin: 0; }
        h2 { font-size: 11pt; margin: 2px 0; font-weight: normal; }
        .meta { font-size: 9pt; color: #555; margin-bottom: 6px; }
        table { width: 100%; border-collapse: collapse; font-size: 9.5pt; }
        th { background: #f0f0f0; border: 1px solid #ddd; padding: 4px 8px; text-align: left; font-size: 9pt; }
        th.amt { text-align: right; }
        td { border: 1px solid #ddd; padding: 4px 8px; }
        td.amt { text-align: right; font-variant-numeric: tabular-nums; }
        tr:nth-child(even) { background: #fafafa; }
        .badge-ok  { color: #166534; background: #dcfce7; padding: 1px 6px; border-radius: 10px; font-size: 8.5pt; }
        .badge-no  { color: #374151; background: #f3f4f6; padding: 1px 6px; border-radius: 10px; font-size: 8.5pt; }
        .summary   { margin-bottom: 8px; font-size: 9.5pt; }
        .summary span { margin-right: 20px; }
        tfoot tr { background: #f0faf8; }
        tfoot td { border-top: 2px solid #99d6c8; font-weight: bold; font-size: 9.5pt; }
      `}</style>

      <div className="header">
        <h1>Daily Waybill Report</h1>
        <h2>{dateLabel}</h2>
        {shipmentType && <p style={{ fontSize: '9pt', margin: 0 }}>Shipment Type: {shipmentType}</p>}
      </div>

      <div className="summary">
        <span>Total: <strong>{waybills.length}</strong></span>
        <span>Delivered: <strong>{delivered}</strong></span>
        <span>Pending: <strong>{pending}</strong></span>
        <span>Total Amount Due: <strong>{fmtCurrency(periodTotal)}</strong></span>
      </div>

      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Serial No.</th>
            <th>Shipper</th>
            <th>Customer</th>
            <th>Date</th>
            <th>Origin</th>
            <th>Destination</th>
            <th>Shipment Type</th>
            <th>Delivered</th>
            <th className="amt">Amount Due</th>
          </tr>
        </thead>
        <tbody>
          {waybills.length === 0 ? (
            <tr><td colSpan={10} style={{ textAlign: 'center', padding: 16 }}>No waybills found.</td></tr>
          ) : waybills.map((w, i) => {
            const custName = w.CustomerId?.CustomerName || w.CustomerId?.name || w.CustomerName || '—';
            return (
              <tr key={w._id}>
                <td>{i + 1}</td>
                <td><strong>{w.SerialNo}</strong></td>
                <td>{w.Shipper || '—'}</td>
                <td>{custName}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(w.TransactionDate)}</td>
                <td>{w.Origin || '—'}</td>
                <td>{w.Destination || '—'}</td>
                <td>{w.ShipmentType || w.shipmentType || '—'}</td>
                <td>{w.IsDelivered ? <span className="badge-ok">Delivered</span> : <span className="badge-no">Pending</span>}</td>
                <td className="amt">{fmtCurrency(w.amountDue)}</td>
              </tr>
            );
          })}
        </tbody>
        {waybills.length > 0 && (
          <tfoot>
            <tr>
              <td colSpan={9} style={{ textAlign: 'right' }}>TOTAL</td>
              <td className="amt">{fmtCurrency(periodTotal)}</td>
            </tr>
          </tfoot>
        )}
      </table>

      <div className="no-print" style={{ marginTop: 16 }}>
        <button onClick={() => window.print()} style={{ padding: '6px 16px' }}>Print</button>
      </div>
    </>
  );
}
